/* eslint-disable react/destructuring-assignment */
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import ErrorPage from 'pages/_error';
import { Article, Content, ContentType } from 'pleinchamp-api-client';
import React, { createContext, RefObject, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ActuLayout } from '@actus/components/ActuLayout/ActuLayout.component';
import { ContentApi, fetchMostViewedNews } from '@api/business/api.utils';
import { MostViewedContent } from '@components/content/MostViewedContent/MostViewedContent.component';
import { FullPageLoader } from '@layout/components/FullPageLoader/FullPageLoader.component';
import { searchContent } from '@search/business/search.utils';
import { userSelectors } from '@user/business/User.store';
import { ApiStatusCode, formatPropsForStatic } from '@utils/api.utils';
import { usePlcRouter } from '@utils/customHooks';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString } from '@utils/url';
import { PlcNextPage, PlcPageContextProps } from 'features/business/app.types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const NB_ACTU_MOST_VIEWED = 3;
const NAMESPACES = [...defaultNamespaces, 'article'];

interface Props {
  slug: string;
  article: Article;
  readAlso: Article[];
  nbComments: number;
  commentsRef: RefObject<HTMLDivElement>;
  mostViewedNews: MostViewedContent[];
  isPreview: boolean;
  isValidating: boolean;
}

function fetchArticle(slug: Props['slug']) {
  return ContentApi.getArticle(slug).then((r) => r.data);
}

function fetchReadAlso(slug: Props['slug']) {
  return ContentApi.getRelatedArticles(slug).then((r) => r.data);
}

export const ActuContext = createContext<PlcPageContextProps<Props>>({
  article: { initialData: undefined, isValidating: false },
  commentsRef: { isValidating: false },
  isPreview: { isValidating: false },
  isValidating: { data: true },
  mostViewedNews: { isValidating: false },
  nbComments: { isValidating: false },
  readAlso: { isValidating: false },
  slug: { data: '', isValidating: false },
});

const ActuPage: PlcNextPage<Partial<Props>> = (props) => {
  const isInit = useSelector(userSelectors.isInit);
  const isPro = useSelector(userSelectors.isPro) || undefined;
  const isProArticle = Boolean(props.article?.isPro);

  const waitForArticle = isPro && isProArticle && !isInit;
  const commentsRef = useRef<HTMLDivElement>(null);
  const [nbComments, setNbComments] = useState<number>();

  const { isFallback } = usePlcRouter();

  if (isFallback) {
    return <FullPageLoader visible={true} />;
  }

  if (!isFallback && !props.article) {
    return <ErrorPage errorCode={ApiStatusCode.notFound} />;
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    author: {
      name: props.article?.signature,
      type: 'Organisation',
    },
    datePublished: props.article?.publishDate,
    description: props.article?.metaDescription,
    headline: props.article?.title,
    image: props.article?.featuredMedia?.url,
    mainEntityOfPage: props.article?.canonicalLink,
    publisher: {
      firstname: props.article?.author.userFirstname,
      lastname: props.article?.author.userLastname,
      partnerIcon: props.article?.author.partnerIcon.url,
      partnerName: props.article?.author.partnerName,
      type: 'Organisation',
    },
  };

  return (
    <ActuContext.Provider
      value={{
        article: { initialData: props.article },
        commentsRef: { data: commentsRef, isValidating: false },
        isPreview: { data: props.isPreview, isValidating: false },
        isValidating: { isValidating: waitForArticle },
        mostViewedNews: { data: props.mostViewedNews },
        nbComments: { data: nbComments, isValidating: false, setData: setNbComments },
        readAlso: { data: props.readAlso },
        slug: { data: props.slug, isValidating: false },
      }}
    >
      <Head>
        <title>{props.article?.title}</title>
        <meta content={props.article?.metaDescription} name="description" />
        <script
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          type="application/ld+json"
        />
        <meta content="max-snippet:-1, max-image-preview:large, max-video-preview:-1" name="robots" />
      </Head>
      <ActuLayout />
    </ActuContext.Provider>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  let articles: Content[];
  try {
    articles = await searchContent({ contentTypes: [ContentType.Article], isPro: false, limit: 20 }).then((r) => {
      return r.contents;
    });
  } catch {
    articles = [];
  }
  const paths = articles.map((a) => ({
    params: { slug: a.slug },
  }));

  return {
    fallback: true,
    paths,
  };
};

export const getStaticProps: GetStaticProps | { redirect: { destination: string; permanent: boolean } } = async ({
  params,
  locale,
}) => {
  const slug = castUrlParamToString(params?.slug);
  if (!slug || slug === '') {
    console.error('Error actualite StaticPropsSide, slug worth : ', slug);
    return <ErrorPage errorCode={ApiStatusCode.notFound} />;
  }

  const article = await fetchArticle(slug)
    .then((result) => result)
    .catch((error: { response: { status: ApiStatusCode } }) => {
      console.error('Error fetchArticle actualite on StaticProps', slug, error);
      if (error && error.response && error.response.status && error.response.status === ApiStatusCode.notFound) {
        return ApiStatusCode.notFound;
      }
      return undefined;
    });

  const props = await Promise.all([
    fetchReadAlso(slug),
    fetchMostViewedNews(NB_ACTU_MOST_VIEWED),
    serverSideTranslations(locale || 'fr', defaultNamespaces),
  ])
    .then(([readAlso, mostViewedNews, i18n]) => ({
      article,
      mostViewedNews,
      readAlso,
      ...i18n,
    }))
    .catch(async (err) => {
      console.error('Error occured on /index.tsx', err);
      return serverSideTranslations(locale || 'fr', NAMESPACES);
    });

  return formatPropsForStatic(props);
};
export default ActuPage;
